import { useState, useEffect, useRef, useCallback } from 'react'
import { SensitiveValue } from '@/components/privacy/SensitiveText'

export interface AnimatedValueProps {
  value: number
  format: 'currency' | 'number' | 'percent' | 'compact-currency'
  duration?: number
  className?: string
}

const DEFAULT_DURATION = 400

/**
 * Ease-out cubic easing function
 * Creates a smooth deceleration effect
 */
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

/**
 * Formats a number as Brazilian Real currency (R$ X.XXX,XX)
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

/**
 * Formats a number with thousand separators using pt-BR locale
 */
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR').format(value)
}

/**
 * Formats a number as percentage with one decimal place
 * Note: Intl.NumberFormat with style: 'percent' multiplies by 100,
 * so we pass the value as-is (e.g., 0.125 for 12.5%)
 */
const formatPercent = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)
}

/**
 * Formats a number as compact Brazilian Real currency (R$ 1.2M, R$ 500K, R$ 123)
 */
const formatCompactCurrency = (value: number): string => {
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)}M`
  }
  if (absValue >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(0)}K`
  }
  return `R$ ${value.toFixed(0)}`
}

/**
 * Custom hook to detect user's preference for reduced motion
 */
const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handleChange = (event: MediaQueryListEvent): void => {
      setPrefersReducedMotion(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return prefersReducedMotion
}

/**
 * AnimatedValue Component
 *
 * Displays numeric values with a smooth count-up animation.
 * Supports currency (BRL), number, and percent formats.
 * Respects user's prefers-reduced-motion preference.
 */
export function AnimatedValue({
  value,
  format,
  duration = DEFAULT_DURATION,
  className,
}: AnimatedValueProps): JSX.Element {
  const [displayValue, setDisplayValue] = useState(value)
  const animationFrameRef = useRef<number | null>(null)
  const startValueRef = useRef(value)
  const prefersReducedMotion = useReducedMotion()

  const getFormatter = useCallback((formatType: 'currency' | 'number' | 'percent' | 'compact-currency') => {
    switch (formatType) {
      case 'currency':
        return formatCurrency
      case 'number':
        return formatNumber
      case 'percent':
        return formatPercent
      case 'compact-currency':
        return formatCompactCurrency
    }
  }, [])

  useEffect(() => {
    // If reduced motion is preferred, skip animation and show final value immediately
    if (prefersReducedMotion) {
      setDisplayValue(value)
      return
    }

    const startValue = startValueRef.current
    const endValue = value
    const startTime = performance.now()

    const animate = (currentTime: number): void => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = easeOutCubic(progress)

      const currentValue = startValue + (endValue - startValue) * easedProgress
      setDisplayValue(currentValue)

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        // Ensure we end exactly on the target value
        setDisplayValue(endValue)
        startValueRef.current = endValue
      }
    }

    // Cancel any existing animation
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    // Start new animation
    animationFrameRef.current = requestAnimationFrame(animate)

    // Cleanup on unmount or when value/duration changes
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [value, duration, prefersReducedMotion])

  // Update startValueRef when animation completes for next animation
  useEffect(() => {
    if (prefersReducedMotion) {
      startValueRef.current = value
    }
  }, [value, prefersReducedMotion])

  const formatter = getFormatter(format)
  const formattedValue = formatter(displayValue)

  // Wrap currency values with privacy protection
  const isCurrency = format === 'currency' || format === 'compact-currency'

  return (
    <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {isCurrency ? (
        <SensitiveValue mask="R$ ••••••">{formattedValue}</SensitiveValue>
      ) : (
        formattedValue
      )}
    </span>
  )
}

export default AnimatedValue
